<script setup>
import { defineAsyncComponent, computed, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Page from '../Layout/Page.vue';
import { useGamePlayStore } from '../../stores/gamePlayStore';
import { useUserStore } from '@/stores/userStore';
import { useDemoStore } from '@/stores/demoStore';
import { gameIdMap } from '../../router/gameIds';

const route = useRoute();
const router = useRouter();
const gamePlayStore = useGamePlayStore();
const userStore = useUserStore();
const demoStore = useDemoStore();

// Keep track of the current game for change detection
let currentGame = '';

// Transform kebab-case slug to camelCase game name
const getGameName = (slug) => {
  // First try direct lookup
  if (gameIdMap[slug]) {
    return gameIdMap[slug];
  }

  // If not found, try removing 'the-' prefix
  if (slug.startsWith('the-')) {
    const withoutThe = slug.replace('the-', '');
    return gameIdMap[withoutThe] || null;
  }

  return null;
};

// Dynamically load the game component based on the current game
const currentGameComponent = computed(() => {
  const gameSlug = route.params.game;
  if (!gameSlug) {
    router.replace('/');
    return null;
  }

  const transformedName = getGameName(gameSlug);
  if (!transformedName) {
    console.warn(`Invalid game slug: ${gameSlug}`);
    router.replace('/');
    return null;
  }

  gamePlayStore.state.currentGame = transformedName;

  // Use @/ alias for consistent path resolution with error handling
  const asyncComp = defineAsyncComponent({
    loader: () =>
      import(`@/components/GamePlay/Games/${transformedName}/Game.vue`),
    loadingComponent: defineAsyncComponent(
      () => import('@/components/GamePlay/Common/Loading.vue')
    ),
    errorComponent: defineAsyncComponent(
      () => import('@/components/Layout/ErrorFallback.vue')
    ),
    delay: 200,
    timeout: 10000,
    onError: (error, retry, fail, attempts) => {
      console.error(`Failed to load game component ${transformedName}:`, error);
      console.error(`Attempt ${attempts}/3`);

      if (attempts <= 3) {
        console.log(`Retrying load for ${transformedName}...`);
        retry();
      } else {
        console.error(
          `Failed to load ${transformedName} after 3 attempts, giving up`
        );
        fail();
      }
    },
  });

  return asyncComp;
});

const gameExited = false;

// Keep track of last game to detect game changes
watchEffect(() => {
  const gameSlug = route.params.game;
  const gameName = getGameName(gameSlug);

  if (gameName && gameName !== currentGame) {
    // If we're switching games, completely reset the demo state
    console.log(
      `Game changed from ${currentGame} to ${gameName}, resetting demo state`
    );
    // This is the critical fix: reset ALL demo state when switching games
    demoStore.resetForGameChange();
    currentGame = gameName;
  }
});
</script>

<template>
  <Page class="flex flex-center game-play">
    <component
      :is="currentGameComponent"
      v-if="!gameExited && currentGameComponent"
    ></component>
  </Page>
</template>
